var __assign = (this && this.__assign) || function () {
    __assign = Object.assign || function(t) {
        for (var s, i = 1, n = arguments.length; i < n; i++) {
            s = arguments[i];
            for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p))
                t[p] = s[p];
        }
        return t;
    };
    return __assign.apply(this, arguments);
};
var __rest = (this && this.__rest) || function (s, e) {
    var t = {};
    for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p) && e.indexOf(p) < 0)
        t[p] = s[p];
    if (s != null && typeof Object.getOwnPropertySymbols === "function")
        for (var i = 0, p = Object.getOwnPropertySymbols(s); i < p.length; i++) {
            if (e.indexOf(p[i]) < 0 && Object.prototype.propertyIsEnumerable.call(s, p[i]))
                t[p[i]] = s[p[i]];
        }
    return t;
};
import React, { useMemo } from 'react';
import { cx, css as emotionCss } from 'emotion';
import { FuzzyHighlighter, Highlighter } from '@citrus/highlighter';
import { getAutomation, getDebug } from '@citrus/runtime';
import { isNotNullOrEmpty } from '@pcty/utils';
import { useTheme } from '@citrus/user';
import { useRadio } from './private/RadioContext';
import { InputWrapper, Label, Radio, RadioChecked, StyledInput, InnerWrapper as XInnerWrapper, InnerWrapperChecked as XInnerWrapperChecked, TextWrapper, } from './Radio.styled';
import { textWrapperDisabled as textWrapperDisabledStyles } from '../styles/legacy';
import { textWrapperDisabled as textWrapperDisabledCitronStyles } from '../styles/citron';
import { styledInputChecked as styledInputCheckedStyles, styledInputDisabled as styledInputDisabledStyles, } from '../styles/citron';
var VERSION = '7.6.1';
/** DISPLAY NAME: &lt;Radio.Item&gt; */
var RadioItem = function (_a) {
    var _b, _c, _d;
    var _e;
    var automationId = _a.automationId, children = _a.children, className = _a.className, css = _a.css, disabled = _a.disabled, value = _a.value, __index = _a.__index, _f = _a.__TYPE, __TYPE = _f === void 0 ? 'RadioItem' : _f, other = __rest(_a, ["automationId", "children", "className", "css", "disabled", "value", "__index", "__TYPE"]);
    var isCitron = useTheme().citrus2022;
    var context = useRadio();
    var classes = cx((_b = {},
        _b[emotionCss(css)] = isNotNullOrEmpty(css),
        _b), className || '');
    var getHighlightable = function (highlighter, disabled, content) {
        if (highlighter === 'disabled')
            return content;
        var disabledHighlight = {
            backgroundColor: '#cccccc',
            color: '#ffffff',
        };
        if (highlighter === 'fuzzy' && typeof content === 'string') {
            return React.createElement(FuzzyHighlighter, { css: disabled ? disabledHighlight : undefined, text: content });
        }
        return React.createElement(Highlighter, { css: disabled ? disabledHighlight : undefined }, content);
    };
    var text = useMemo(function () { var _a, _b; return getHighlightable((_a = context === null || context === void 0 ? void 0 : context.highlighter) !== null && _a !== void 0 ? _a : 'exact', (_b = (disabled || (context === null || context === void 0 ? void 0 : context.disabled))) !== null && _b !== void 0 ? _b : false, children); }, [context, disabled, children]);
    var isChecked = (context === null || context === void 0 ? void 0 : context.selectedIndex) === __index;
    var isDisabled = disabled || (context === null || context === void 0 ? void 0 : context.disabled);
    var commonProps = __assign({ 'aria-checked': isChecked, checked: isChecked, disabled: disabled || (context === null || context === void 0 ? void 0 : context.disabled), label: children, name: context === null || context === void 0 ? void 0 : context.groupId, onChange: function () { return context === null || context === void 0 ? void 0 : context.onChange({ id: context.groupId, index: __index, value: value }); }, type: 'radio', value: value }, getAutomation(automationId));
    var InnerWrapper = isChecked ? XInnerWrapperChecked : XInnerWrapper;
    return (React.createElement(Label, __assign({ "aria-labelledby": "".concat(children, "-for-labelledby"), className: classes, columns: (_e = context === null || context === void 0 ? void 0 : context.columns) !== null && _e !== void 0 ? _e : 1 }, other, getDebug(sessionStorage.getItem('citrus-debug') === 'true', __TYPE, VERSION)),
        React.createElement(InnerWrapper, null,
            React.createElement(InputWrapper, null,
                isCitron && isChecked && React.createElement(RadioChecked, __assign({}, commonProps)),
                (!isCitron || !isChecked) && React.createElement(Radio, __assign({}, commonProps)),
                isCitron && (React.createElement(StyledInput, { className: cx((_c = {},
                        _c[emotionCss(styledInputCheckedStyles)] = isChecked,
                        _c[emotionCss(styledInputDisabledStyles)] = isDisabled,
                        _c)) }))),
            React.createElement(TextWrapper, { className: cx((_d = {},
                    _d[emotionCss(isCitron ? textWrapperDisabledCitronStyles : textWrapperDisabledStyles)] = disabled || (context === null || context === void 0 ? void 0 : context.disabled),
                    _d)), id: "".concat(children, "-for-labelledby") }, text))));
};
RadioItem.defaultProps = {
    __TYPE: 'RadioItem',
};
export default RadioItem;
